import { useState, useEffect, useCallback } from "react";
import {
  listPermissions,
  type PermissionResponse,
  type PermissionsMeta,
} from "../api/accessManagement/permissionsService";

export interface PermissionItem {
  id: string;
  code: string;
  description: string;
  resource?: string;
  action?: string;
  scopeType?: string;
}

function mapToPermissionItem(p: PermissionResponse): PermissionItem {
  const id = p._id ?? p.id ?? p.code;
  const code = p.code ?? p.permission ?? "";
  return {
    id: String(id),
    code,
    description: p.description ?? "",
    resource: p.resource,
    action: p.action,
    scopeType: p.scopeType,
  };
}

export function usePermissions(search: string = "") {
  const [permissions, setPermissions] = useState<PermissionItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [meta, setMeta] = useState<PermissionsMeta>({
    total: 0,
    page: 1,
    limit: 20,
    pages: 0,
  });

  const fetchPermissions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, meta } = await listPermissions({
        search: search && search.trim() ? search : undefined,
        page,
        limit: pageSize,
      });
      const items = data.map(mapToPermissionItem);
      setPermissions(items);
      setMeta(meta);
    } catch (err) {
      console.error("Failed to fetch permissions:", err);
      setError(
        err instanceof Error ? err.message : "Failed to fetch permissions"
      );
      setPermissions([]);
    } finally {
      setLoading(false);
    }
  }, [search, page, pageSize]);

  useEffect(() => {
    fetchPermissions();
  }, [fetchPermissions]);

  return {
    permissions,
    loading,
    error,
    page,
    pageSize,
    totalCount: meta.total,
    totalPages: meta.pages || 1,
    setPage,
    setPageSize,
    refetch: fetchPermissions,
  };
}
